import { RequestClassifier, RequestClassifierResult } from "../../core/request-classifier.js";
import { ExecutionPlanner, ExecutionPlan } from "../../core/execution-planner.js";
import { WorkflowStateMachine } from "../../core/workflow-state-machine.js";
import { BranchGate } from "../../core/gates/branch-gate.js";
import { EvidenceLedger } from "../../core/evidence/evidence-ledger.js";
import { DelegationController } from "../../core/delegation-controller.js";
import { OpenCodeAdapter } from "../../core/runtime/opencode-adapter.js";
import { runCollectEvidence } from "./collect-evidence.js";
import { QualityGuard } from "../../core/validation/quality-guard.js";
import { HandoffEngine } from "../../core/handoff/handoff-engine.js";
import { HealerEngine } from "../../core/healing/healer-engine.js";
import { createRuntimeRemediationExecutor } from "../../core/healing/runtime-remediation-executor.js";
import { isRecoverableGateFailure, isTerminalFailure } from "../../core/statuses.js";
import { Finalizer } from "../../core/finalization/finalizer.js";
import fs from "node:fs/promises";
import path from "node:path";
import { execSync } from "node:child_process";

/** Snapshot of workspace state before/after read-only execution. */
interface ReadOnlyWorkspaceState {
  snapshot: { files: Record<string, { sha256: string }> };
  head: string;
  branch: string;
  indexTree: string;
  porcelainStatus: string;
}

/** Combined result from evidence collection and quality guard. */
interface ValidationResult {
  overallStatus: string;
  evidence: Record<string, unknown>;
  quality: Record<string, unknown>;
}

/** Result returned by BranchGate.check(). */
interface BranchGateResult {
  blocked: boolean;
  reason: string;
  branch: string;
  branchBefore: string;
  recovered: boolean;
}

async function captureReadOnlyState(cwd: string, fastTrack = false) {
  const finalizer = new Finalizer({ cwd, fastTrack });
  const snapshot = await finalizer.snapshotManager.capture();
  
  const execGit = (args: string) => {
    try {
      return execSync(`git ${args}`, { cwd, encoding: "utf8", stdio: "pipe" }).trim();
    } catch {
      return "";
    }
  };

  const head = execGit("rev-parse HEAD");
  const branch = execGit("rev-parse --abbrev-ref HEAD");
  const indexTree = execGit("write-tree");
  const porcelainStatus = execGit("status --porcelain");

  return {
    snapshot,
    head,
    branch,
    indexTree,
    porcelainStatus
  };
}

function compareReadOnlyState(before: ReadOnlyWorkspaceState, after: ReadOnlyWorkspaceState) {
  const violations: string[] = [];

  // 1. Compare branch
  if (before.branch !== after.branch) {
    violations.push(`Branch changed from '${before.branch}' to '${after.branch}'`);
  }

  // 2. Compare HEAD
  if (before.head !== after.head) {
    violations.push(`Git HEAD changed from '${before.head}' to '${after.head}' (commit or reset made inside read-only task)`);
  }

  // 3. Compare index tree
  if (before.indexTree !== after.indexTree) {
    violations.push(`Git index tree changed from '${before.indexTree}' to '${after.indexTree}'`);
  }

  // 4. Compare porcelain status
  if (before.porcelainStatus !== after.porcelainStatus) {
    violations.push(`Git status changed. Before:\n${before.porcelainStatus}\nAfter:\n${after.porcelainStatus}`);
  }

  // 5. Compare workspace files (SHA-256 hashes)
  const beforeFiles = before.snapshot.files || {};
  const afterFiles = after.snapshot.files || {};

  for (const [file, info] of Object.entries(afterFiles)) {
    const prevInfo = beforeFiles[file];
    if (!prevInfo) {
      violations.push(`File added: ${file}`);
    } else if (prevInfo.sha256 !== (info as any).sha256) {
      violations.push(`File content modified: ${file}`);
    }
  }
  for (const file of Object.keys(beforeFiles)) {
    if (!afterFiles[file]) {
      violations.push(`File deleted: ${file}`);
    }
  }

  return violations;
}

function slugify(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 32) || "task";
}

function combineValidation(evidence: Record<string, unknown>, quality: Record<string, unknown>): ValidationResult {
  const statuses = [evidence.internalStatus, quality.overallStatus];
  let overallStatus = "PASS";
  if (statuses.includes("BLOCKED")) overallStatus = "BLOCKED";
  else if (statuses.includes("FAIL")) overallStatus = "FAIL";
  else if (statuses.includes("FAIL_DELEGATION_GATE")) overallStatus = "FAIL_DELEGATION_GATE";
  else if (statuses.includes("FAIL_QUALITY_GATE")) overallStatus = "FAIL_QUALITY_GATE";
  else if (statuses.includes("PASS_WITH_NOTES")) overallStatus = "PASS_WITH_NOTES";
  return { overallStatus, evidence, quality };
}

export interface ExecuteOptions {
  cwd: string;
  naturalRequest: string;
  taskSlug?: string;
}

/**
 * classifyAndPlanExecution - Intake analysis and contract formulation.
 */
async function classifyAndPlanExecution(
  naturalRequest: string,
  cwd: string,
  taskSlug: string,
  delegationController: DelegationController,
  stateMachine: WorkflowStateMachine
): Promise<{ plan: ExecutionPlan; classification: RequestClassifierResult }> {
  const classifier = new RequestClassifier();
  const classification = classifier.classify(naturalRequest, { cwd });
  console.log(`[CLASSIFIED] Intent: ${classification.intent}, Mode: ${classification.mode}, Profile: ${classification.profile}`);
  stateMachine.transitionTo("CLASSIFIED");

  await delegationController.route(naturalRequest, classification);

  const routingDecision = classification.routingDecision || {};
  if (routingDecision.permissionDecision === "blocked") {
    stateMachine.transitionTo("BLOCKED");
    throw new Error(`[ROUTE BLOCKED] ${routingDecision.reason}`);
  }
  if (routingDecision.permissionDecision === "rerouted") {
    console.log(`[REROUTED] ${routingDecision.reason}`);
  }

  const planner = new ExecutionPlanner({ cwd });
  const plan = planner.plan(classification, taskSlug);
  console.log(`[PLANNED] Owner: ${plan.owner}, Remediation limit: ${plan.remediationLimit}`);
  stateMachine.transitionTo("PLANNED");

  return { plan, classification };
}

/**
 * runBranchGate - Authorizes changes within the branch lifecycle.
 */
function runBranchGate(plan: ExecutionPlan, taskSlug: string, cwd: string, stateMachine: WorkflowStateMachine, branchGate: BranchGate): BranchGateResult {
  const gateResult = branchGate.check("", {
    taskSlug,
    readOnly: !plan.branchNeeded
  });

  if (gateResult.blocked) {
    stateMachine.transitionTo("BLOCKED");
    throw new Error(`[GATE BLOCKED] ${gateResult.reason}`);
  }
  console.log(`[PASS] Branch Gate: ${gateResult.recovered ? `${gateResult.branchBefore} -> ${gateResult.branch}` : `${gateResult.branch} is authorized`}.`);
  stateMachine.transitionTo("BRANCH_READY");
  return gateResult;
}

/**
 * createSpecIfRequired - Implements Spec-Driven Development gate.
 */
async function createSpecIfRequired(plan: ExecutionPlan, classification: RequestClassifierResult, taskSlug: string, cwd: string): Promise<void> {
  if (plan.specPath) {
    const fullSpecPath = path.join(cwd, plan.specPath);
    const specExists = await fs.access(fullSpecPath).then(() => true).catch(() => false);
    if (!specExists) {
      await fs.mkdir(path.dirname(fullSpecPath), { recursive: true });
      const specTemplatePath = path.join(cwd, ".ai-workflow/templates/specs/standard.md");
      const templateContent = await fs.readFile(specTemplatePath, "utf8").catch(() => {
        return `# [STANDARD] Specification: ${classification.request}\n\n## Metadata\n\n- ID: SPEC-${taskSlug}\n- Author: Spec-Engineer\n- Status: DRAFT\n- Date: ${new Date().toISOString().split("T")[0]}\n\n## Functional Requirements\n\n- ${classification.request}\n\n## Technical Implementation Plan\n\n### Files to Create/Modify\n\n- \`src/index.js\`\n\n## Acceptance Criteria\n\n- [ ] Implemented successfully\n\n## Testing Strategy\n\n- [ ] Behavior tests pass`;
      });
      await fs.writeFile(fullSpecPath, templateContent);
      console.log(`[EXECUTE] Created DRAFT specification template at: ${plan.specPath}`);
    }
  }
}

/**
 * runImplementation - Delegations to the implementation engine.
 */
async function runImplementation(
  plan: ExecutionPlan,
  classification: RequestClassifierResult,
  delegationController: DelegationController,
  stateMachine: WorkflowStateMachine,
  fastTrack: boolean = false
): Promise<any> {
  stateMachine.transitionTo("DELEGATED");
  stateMachine.transitionTo("IMPLEMENTING");

  let promptMsg = classification.request;
  if (plan.specPath) {
    promptMsg = `Please review and fill in the specification file at: ${plan.specPath}. Make sure to change the Status field to 'APPROVED' in the Metadata section, and then implement the behavior described.`;
  }

  const runResult = await delegationController.implement(promptMsg, plan.owner, { readOnly: !plan.branchNeeded, fastTrack });

  if (!runResult.success) {
    console.error(`[EXECUTE] OpenCode adapter execution reported failure: ${runResult.error || "Unknown error"}`);
    stateMachine.transitionTo("BLOCKED");
    throw new Error(`[WORKFLOW BLOCKED] OpenCode runtime execution failed: ${runResult.error || "Unknown error"}`);
  }
  return runResult;
}

/**
 * runReadOnlyConfinementCheck - Asserts read-only execution constraints.
 */
async function runReadOnlyConfinementCheck(readOnlyStateBefore: ReadOnlyWorkspaceState, cwd: string, stateMachine: WorkflowStateMachine, fastTrack = false): Promise<void> {
  const readOnlyStateAfter = await captureReadOnlyState(cwd, fastTrack);
  const violations = compareReadOnlyState(readOnlyStateBefore, readOnlyStateAfter);
  if (violations.length > 0) {
    console.error(`[EXECUTE] Read-only confinement violation detected:`);
    for (const v of violations) {
      console.error(`  - ${v}`);
    }
    stateMachine.transitionTo("BLOCKED");
    throw new Error(`[WORKFLOW BLOCKED] Read-only confinement violation: files were written/modified, index or commits modified: \n${violations.join("\n")}`);
  }
}

/**
 * runValidation - Resolves evidence validation contracts.
 */
async function runValidation(
  plan: ExecutionPlan,
  taskSlug: string,
  cwd: string,
  gateResult: BranchGateResult,
  naturalRequest: string,
  delegationController: DelegationController,
  stateMachine: WorkflowStateMachine
): Promise<{ result: ValidationResult; validateWorkflow: () => Promise<ValidationResult> }> {
  stateMachine.transitionTo("VALIDATING");

  const validateWorkflow = async () => {
    const evidence = await runCollectEvidence({
      cwd,
      exitOnError: false,
      taskSlug,
      evidencePolicy: plan.evidencePolicy,
      profile: plan.profile,
      branchRecovery: gateResult.recovered ? `${gateResult.branchBefore} -> ${gateResult.branch}` : "NOT_REQUIRED",
      userRequest: naturalRequest,
      explicitApprovals: process.env.AI_WORKFLOW_APPROVALS ? process.env.AI_WORKFLOW_APPROVALS.split(",").map(a => a.trim()) : []
    });
    const quality = await new QualityGuard({
      cwd,
      taskSlug,
      evidencePolicy: plan.evidencePolicy,
      riskLevel: plan.riskLevel
    }).verify();
    return combineValidation(evidence, quality);
  };

  const result = await delegationController.validate(taskSlug, plan.profile, validateWorkflow, plan.evidencePolicy);
  return { result, validateWorkflow };
}

/**
 * runBoundedRemediation - Heals gate violations.
 */
async function runBoundedRemediation(
  plan: any,
  taskSlug: string,
  cwd: string,
  initialResult: any,
  validateWorkflow: () => Promise<any>,
  ledger: EvidenceLedger,
  delegationController: DelegationController,
  stateMachine: WorkflowStateMachine,
  checkBranchSafety: () => void,
  updateSnapshot: () => Promise<void>
): Promise<any> {
  if (!isRecoverableGateFailure(initialResult.overallStatus)) {
    return initialResult;
  }

  console.log(`\n[REMEDIATION REQUIRED] ${initialResult.overallStatus}. Starting bounded remediation (limit: ${plan.remediationLimit}).`);
  const executor = createRuntimeRemediationExecutor(cwd, ledger);
  const healer = new HealerEngine({
    cwd,
    remediationLimit: plan.remediationLimit,
    taskSlug,
    ledger
  });

  return await healer.run({
    initialResult,
    validate: async () => {
      stateMachine.transitionTo("REVALIDATING");
      return await delegationController.validate(taskSlug, plan.profile, validateWorkflow, plan.evidencePolicy);
    },
    remediate: async (remedCtx) => {
      stateMachine.transitionTo("REMEDIATING");
      const remRes = await executor(remedCtx as any);
      if (remRes.applied) {
        checkBranchSafety();
        await updateSnapshot();
      }
      return remRes;
    }
  });
}

/**
 * runFinalizerStabilization - Verifies integrity and fidelity.
 */
async function runFinalizerStabilization(
  plan: any,
  taskSlug: string,
  cwd: string,
  initialResult: any,
  validateWorkflow: () => Promise<any>,
  delegationController: DelegationController,
  finalizer: Finalizer,
  lastWriteSnapshotRef: { current: any },
  checkBranchSafety: () => void
): Promise<{ result: any; integrity: any }> {
  let integrity = await finalizer.verifyIntegrity(lastWriteSnapshotRef.current);
  let revalidationCount = 0;
  const maxRevalidations = 2;
  let result = initialResult;

  while (!integrity.valid && revalidationCount < maxRevalidations) {
    console.log(`\n[FINALIZER WARNING] Workspace mutated during validation! Re-running validation to stabilize.`);
    console.log(`Changes detected:`, integrity.changes);

    checkBranchSafety();

    lastWriteSnapshotRef.current = await finalizer.snapshotManager.capture();
    revalidationCount++;

    result = await delegationController.validate(taskSlug, plan.profile, validateWorkflow, plan.evidencePolicy);

    checkBranchSafety();

    integrity = await finalizer.verifyIntegrity(lastWriteSnapshotRef.current);
  }

  return { result, integrity };
}

/**
 * generateFinalHandoff - Generates Handoff report.
 */
async function generateFinalHandoff(plan: ExecutionPlan, taskSlug: string, cwd: string, finalState: string, evidence: Record<string, unknown> | null): Promise<string> {
  console.log("\n--- Final Handoff ---");
  const handoffEngine = new HandoffEngine({ cwd });
  const handoffPath = await handoffEngine.generate({
    taskId: taskSlug,
    status: finalState,
    specPaths: plan.specPath ? [plan.specPath] : [],
    evidence,
    nextActions: `Profile ${plan.profile}. Observed execution completed with status ${finalState}.`
  });

  console.log(`\n[AI WORKFLOW COMPLETE] ${finalState}`);
  console.log(`Handoff Packet: ${path.relative(cwd, handoffPath)}\n`);
  return handoffPath;
}

async function runFastTrackValidation(cwd: string, result: any, checkBranchSafety: () => void): Promise<string> {
  console.log(`\n--- [FAST-TRACK] Running Lightweight Validation Gates ---`);
  checkBranchSafety();
  
  const changedFiles = result.evidence?.changedFiles || [];
  console.log(`[FAST-TRACK] Changed files detected:`, changedFiles);
  
  const evidencePath = path.join(cwd, "EVIDENCE.json");
  const hasEvidence = await fs.access(evidencePath).then(() => true).catch(() => false);
  
  if (hasEvidence) {
    const { EvidenceValidator } = await import("../../core/validation/evidence-validator.js");
    const isTest = process.env.NODE_ENV === "test" || process.env.VITEST === "true";
    const evidenceValidator = new EvidenceValidator({ cwd, skipFileCheck: isTest, skipGitCheck: isTest });
    const valResult = await evidenceValidator.validate();
    if (!valResult.valid) {
      console.error(`\n[FAST-TRACK BLOCKED] EVIDENCE.json validation failed: ${valResult.reason}`);
      return "FAIL_QUALITY_GATE";
    }
  }
  return result.overallStatus;
}

async function runFidelityValidation(naturalRequest: string, result: any, finalizer: Finalizer): Promise<string> {
  const fidelityResult = await finalizer.verifyFidelity({
    userRequest: naturalRequest,
    projectContext: result.evidence?.deliveryDecision?.projectContext || null,
    deliveryDecision: result.evidence?.deliveryDecision || null,
    changedFiles: result.evidence?.changedFiles || [],
    evidence: result.evidence || null,
    explicitApprovals: process.env.AI_WORKFLOW_APPROVALS ? process.env.AI_WORKFLOW_APPROVALS.split(",").map(a => a.trim()) : []
  });
  
  if (!fidelityResult.passed) {
    console.log(`\n[FINALIZER BLOCKED] BLOCKED: Artifact fidelity check failed!`);
    console.log(`Reason: ${fidelityResult.reason}`);
    return "FAIL_QUALITY_GATE";
  }
  return result.overallStatus;
}

function computeFinalState(finalStatus: string): string {
  if (finalStatus === "PASS") return "COMPLETED";
  if (finalStatus === "PASS_WITH_NOTES") return "COMPLETED_WITH_NOTES";
  return "BLOCKED";
}

async function executeReadOnlyWorkflow(
  cwd: string,
  stateMachine: WorkflowStateMachine,
  readOnlyStateBefore: ReadOnlyWorkspaceState,
  fastTrack: boolean
): Promise<any> {
  await runReadOnlyConfinementCheck(readOnlyStateBefore, cwd, stateMachine, fastTrack);

  stateMachine.transitionTo("IMPLEMENTED");
  stateMachine.transitionTo("VALIDATING");
  stateMachine.transitionTo("COMPLETED");

  console.log("\n--- Final Handoff ---");
  console.log(`\n[AI WORKFLOW COMPLETE] COMPLETED`);
  console.log(`Handoff Packet: [IN-MEMORY] (No handoff file written in read-only mode)\n`);

  return {
    overallStatus: "PASS",
    evidence: {
      internalStatus: "PASS",
      commandsRun: []
    },
    quality: {
      overallStatus: "PASS"
    },
    stateHistory: stateMachine.getHistory()
  };
}

/**
 * runExecute - Coordinators natural request execution.
 */
export async function runExecute({ cwd, naturalRequest, taskSlug: taskSlugOverride }: ExecuteOptions): Promise<any> {
  if (!naturalRequest || !naturalRequest.trim()) {
    throw new Error("Missing request. Please provide a natural request string via positional arguments or --request flag.");
  }

  const taskSlug = taskSlugOverride || slugify(naturalRequest);
  console.log(`\n[AI WORKFLOW] Executing natural request intake [slug: ${taskSlug}]...\n`);

  const ledger = new EvidenceLedger({ cwd, workflowId: taskSlug });
  const delegationController = new DelegationController({ cwd, ledger, adapter: new OpenCodeAdapter({ cwd }) });

  let plan: ExecutionPlan | null = null;
  try {
    const stateMachine = new WorkflowStateMachine();

    const { plan: planObj, classification } = await classifyAndPlanExecution(naturalRequest, cwd, taskSlug, delegationController, stateMachine);
    plan = planObj;
    const fastTrack = plan.riskLevel === "low" && (!plan.branchNeeded || plan.evidencePolicy === "optional");

    const branchGate = new BranchGate({ memoryDir: path.join(cwd, ".ai-workflow"), cwd });
    const gateResult = runBranchGate(plan, taskSlug, cwd, stateMachine, branchGate);

    const checkBranchSafety = () => {
      if (!planObj.branchNeeded) return;
      const currentBranch = branchGate.getCurrentBranch();
      if (branchGate.protectedBranches.includes(currentBranch)) {
        stateMachine.transitionTo("BLOCKED");
        throw new Error(`[WORKFLOW BLOCKED] Security violation: current branch is protected '${currentBranch}'!`);
      }
    };

    await createSpecIfRequired(plan, classification, taskSlug, cwd);

    let readOnlyStateBefore: ReadOnlyWorkspaceState | null = null;
    if (!plan.branchNeeded) {
      readOnlyStateBefore = await captureReadOnlyState(cwd, fastTrack);
    }

    await runImplementation(plan, classification, delegationController, stateMachine, fastTrack);

    if (!plan.branchNeeded && readOnlyStateBefore) {
      return await executeReadOnlyWorkflow(cwd, stateMachine, readOnlyStateBefore, fastTrack);
    }

    stateMachine.transitionTo("IMPLEMENTED");
    checkBranchSafety();

    const finalizer = new Finalizer({ cwd, fastTrack });
    const snapshotRef = { current: await finalizer.snapshotManager.capture() };

    const { result: valResult, validateWorkflow } = await runValidation(plan, taskSlug, cwd, gateResult, naturalRequest, delegationController, stateMachine);
    let result = valResult;

    result = await runBoundedRemediation(
      plan,
      taskSlug,
      cwd,
      result,
      validateWorkflow,
      ledger,
      delegationController,
      stateMachine,
      checkBranchSafety,
      async () => {
        snapshotRef.current = await finalizer.snapshotManager.capture();
      }
    );

    if (isTerminalFailure(result.overallStatus)) {
      stateMachine.transitionTo("BLOCKED");
      throw new Error(`[WORKFLOW BLOCKED] ${result.overallStatus}: Validation could not be resolved safely.`);
    }

    checkBranchSafety();

    let integrity = { valid: true, changes: { added: [], modified: [], deleted: [] } };
    if (!fastTrack) {
      const stab = await runFinalizerStabilization(
        plan,
        taskSlug,
        cwd,
        result,
        validateWorkflow,
        delegationController,
        finalizer,
        snapshotRef,
        checkBranchSafety
      );
      result = stab.result;
      integrity = stab.integrity;
    }

    let finalStatus = result.overallStatus;
    
    if (fastTrack) {
      finalStatus = await runFastTrackValidation(cwd, result, checkBranchSafety);
    } else if (!integrity.valid) {
      console.log(`\n[FINALIZER BLOCKED] BLOCKED: workspace did not stabilize`);
      console.log(`Final changes detected:`, integrity.changes);
      finalStatus = "FAIL_QUALITY_GATE";
    } else {
      finalStatus = await runFidelityValidation(naturalRequest, result, finalizer);
    }

    const finalState = computeFinalState(finalStatus);
    stateMachine.transitionTo(finalState);

    await generateFinalHandoff(plan, taskSlug, cwd, finalState, result.evidence);

    return { ...result, overallStatus: finalStatus, stateHistory: stateMachine.getHistory() };
  } finally {
    if (plan && !plan.branchNeeded) {
      console.log(`\n[READ-ONLY LEDGER] (In-Memory/Stdout)\n${JSON.stringify(ledger.getEvents(), null, 2)}\n`);
    } else {
      try {
        await ledger.save(`.ai-workflow/history/${taskSlug}-ledger.json`);
      } catch (err) {
        // ignore
      }
    }
  }
}
